import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import ReactSelect from "react-select";
import CountryFlag from "react-country-flag";

// Lista wszystkich krajów do wyboru (najczęściej ISO 3166-1 alpha-2)
import allCountries from "./countryList.json";

const MODES = [
  { value: "FCL", label: "FCL" },
  { value: "LCL", label: "LCL" },
];

const FCL_CONTAINER_TYPES = [
  { value: "20ft", label: "20ft" },
  { value: "40ft", label: "40ft" },
];

function getInitialForm() {
  return {
    countryCode: "",
    city: "",
    roadPrice: "",
    mode: "FCL",
    containerType: "20ft",
    minWeight: "",
    maxWeight: "",
    minVolume: "",
    maxVolume: "",
    comment: "",
    active: true,
  };
}

export default function AdminRoadPricing() {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState({
    mode: "",
    countryCode: "",
    city: "",
    active: "",
    sort: "countryCode"
  });

  const [form, setForm] = useState(getInitialForm());
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    fetchData();
    async function fetchData() {
      const snap = await getDocs(collection(db, "roadPricing"));
      let arr = [];
      snap.forEach(doc => arr.push({ id: doc.id, ...doc.data() }));
      setData(arr);
    }
  }, []);

  const countryOptions = allCountries.map(c => ({
    value: c.code,
    label: (
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <CountryFlag countryCode={c.code} svg style={{ width: 18, height: 18 }} />
        <span>{c.code} – {c.label}</span>
      </span>
    ),
  }));

  const cityList = [
    ...new Set(data.filter(i => !filter.countryCode || i.countryCode === filter.countryCode).map(item => item.city))
  ].sort();

  function getFilteredGrouped() {
    let arr = data.slice();
    if (filter.mode) arr = arr.filter(d => d.mode === filter.mode);
    if (filter.countryCode) arr = arr.filter(d => d.countryCode === filter.countryCode);
    if (filter.city) arr = arr.filter(d => d.city === filter.city);
    if (filter.active !== "") arr = arr.filter(d => String(!!d.active) === filter.active);

    const groups = {};
    arr.forEach(item => {
      const groupKey = `${item.countryCode || ""}|${item.city || ""}|${item.mode}`;
      if (!groups[groupKey]) groups[groupKey] = { items: [], group: item };
      groups[groupKey].items.push(item);
    });

    const groupArr = Object.values(groups).sort((a, b) => {
      if (a.group.countryCode < b.group.countryCode) return -1;
      if (a.group.countryCode > b.group.countryCode) return 1;
      if (a.group.city < b.group.city) return -1;
      if (a.group.city > b.group.city) return 1;
      if (a.group.mode < b.group.mode) return -1;
      if (a.group.mode > b.group.mode) return 1;
      return 0;
    });
    return groupArr;
  }

  const clearForm = () => {
    setForm(getInitialForm());
    setEditId(null);
    setError("");
  };

  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
  };

  const validateForm = () => {
    if (!form.countryCode) return "Wybierz kraj";
    if (!form.city) return "Podaj miasto";
    if (!form.roadPrice || isNaN(form.roadPrice)) return "Podaj prawidłową cenę";
    if (!form.mode) return "Wybierz tryb transportu";
    if (form.mode === "FCL" && !form.containerType) return "Wybierz kontener";
    if (form.mode === "LCL") {
      if (form.minWeight && form.maxWeight && Number(form.minWeight) > Number(form.maxWeight))
        return "Min. waga nie może być większa od maksymalnej";
      if (form.minVolume && form.maxVolume && Number(form.minVolume) > Number(form.maxVolume))
        return "Min. objętość nie może być większa od maksymalnej";
      const overlaps = data.filter(
        d =>
          d.mode === "LCL" &&
          d.countryCode === form.countryCode &&
          d.city === form.city &&
          d.active &&
          (
            ((form.minWeight === "" || d.maxWeight === undefined || Number(form.minWeight) <= Number(d.maxWeight)) &&
              (form.maxWeight === "" || d.minWeight === undefined || Number(form.maxWeight) >= Number(d.minWeight))) &&
            ((form.minVolume === "" || d.maxVolume === undefined || Number(form.minVolume) <= Number(d.maxVolume)) &&
              (form.maxVolume === "" || d.minVolume === undefined || Number(form.maxVolume) >= Number(d.minVolume)))
          ) &&
          (!editId || d.id !== editId)
      );
      if (overlaps.length > 0) return "Dla tego miasta i kraju istnieje już wpis z pokrywającym się zakresem wag/objętości.";
    }
    if (form.mode === "FCL") {
      const duplicate = data.find(
        d =>
          d.mode === "FCL" &&
          d.countryCode === form.countryCode &&
          d.city === form.city &&
          d.containerType === form.containerType &&
          d.active &&
          (!editId || d.id !== editId)
      );
      if (duplicate) return "Dla tego miasta/kraju i kontenera już istnieje aktywny wpis.";
    }
    return "";
  };

  const handleAddOrEdit = async (e) => {
    e.preventDefault();
    const msg = validateForm();
    if (msg) {
      setError(msg);
      return;
    }
    let entry = { ...form, roadPrice: Number(form.roadPrice) };
    if (form.mode === "LCL") {
      entry.minWeight = form.minWeight ? Number(form.minWeight) : 0;
      entry.maxWeight = form.maxWeight ? Number(form.maxWeight) : Infinity;
      entry.minVolume = form.minVolume ? Number(form.minVolume) : 0;
      entry.maxVolume = form.maxVolume ? Number(form.maxVolume) : Infinity;
      delete entry.containerType;
    }
    if (form.mode === "FCL") {
      entry.containerType = form.containerType;
      delete entry.minWeight; delete entry.maxWeight; delete entry.minVolume; delete entry.maxVolume;
    }
    if (editId) {
      await updateDoc(doc(db, "roadPricing", editId), entry);
    } else {
      await addDoc(collection(db, "roadPricing"), entry);
    }
    const snap = await getDocs(collection(db, "roadPricing"));
    let arr = [];
    snap.forEach(doc => arr.push({ id: doc.id, ...doc.data() }));
    setData(arr);
    clearForm();
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setForm({
      countryCode: item.countryCode || "",
      city: item.city,
      roadPrice: item.roadPrice,
      mode: item.mode,
      containerType: item.containerType || "20ft",
      minWeight: item.minWeight !== undefined ? item.minWeight : "",
      maxWeight: item.maxWeight !== undefined ? item.maxWeight : "",
      minVolume: item.minVolume !== undefined ? item.minVolume : "",
      maxVolume: item.maxVolume !== undefined ? item.maxVolume : "",
      comment: item.comment || "",
      active: typeof item.active === "boolean" ? item.active : true,
    });
    setError("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Usunąć ten wpis?")) return;
    await deleteDoc(doc(db, "roadPricing", id));
    setData(data.filter(d => d.id !== id));
    clearForm();
  };

  const handleToggleActive = async (item) => {
    await updateDoc(doc(db, "roadPricing", item.id), { active: !item.active });
    setData(data.map(d => d.id === item.id ? { ...d, active: !d.active } : d));
  };
  // Edycja komentarza podzakresu na żywo
  const handleRangeCommentChange = async (id, val) => {
    setData(data => data.map(d => d.id === id ? { ...d, comment: val } : d));
    await updateDoc(doc(db, "roadPricing", id), { comment: val });
  };

  // Masowa dezaktywacja/aktywacja
  const handleToggleAll = async (items, active) => {
    for (const sub of items) {
      if (!!sub.active !== active) {
        await updateDoc(doc(db, "roadPricing", sub.id), { active });
      }
    }
    const snap = await getDocs(collection(db, "roadPricing"));
    let arr = [];
    snap.forEach(doc => arr.push({ id: doc.id, ...doc.data() }));
    setData(arr);
  };

  // Czy cała grupa jest nieaktywna
  const isGroupInactive = items => items.every(i => !i.active);

  return (
    <div className="max-w-6xl mx-auto bg-white p-6 rounded shadow">
      <h2 className="text-xl font-bold mb-6">Cennik dostaw drogowych</h2>
      {/* FILTRY */}
      <div className="flex gap-4 mb-4 items-center flex-wrap">
        <div className="w-56">
          <ReactSelect
            options={countryOptions}
            value={countryOptions.find(c => c.value === filter.countryCode) || null}
            onChange={val => setFilter(f => ({ ...f, countryCode: val ? val.value : "" }))}
            placeholder="Kraj"
            isClearable
            className="react-select-container"
            classNamePrefix="react-select"
            menuPlacement="auto"
          />
        </div>
        <select className="input w-44" value={filter.city} onChange={e => setFilter(f => ({ ...f, city: e.target.value }))}>
          <option value="">Wszystkie miasta</option>
          {cityList.map(city => <option key={city} value={city}>{city}</option>)}
        </select>
        <select className="input w-36" value={filter.mode} onChange={e => setFilter(f => ({ ...f, mode: e.target.value }))}>
          <option value="">Wszystkie tryby</option>
          {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <select className="input w-36" value={filter.active} onChange={e => setFilter(f => ({ ...f, active: e.target.value }))}>
          <option value="">Aktywne i nieaktywne</option>
          <option value="true">Tylko aktywne</option>
          <option value="false">Tylko nieaktywne</option>
        </select>
        <button className="btn-secondary" onClick={clearForm}>Wyczyść formularz</button>
      </div>
      {/* FORMULARZ */}
      {/* ...formularz bez zmian... */}
      <form className="bg-blue-50 rounded-xl p-4 mb-4" onSubmit={handleAddOrEdit}>
         <div className="flex gap-2 flex-wrap items-end">
          <div className="w-56">
            <label className="block font-semibold mb-1">Kraj*</label>
            <ReactSelect
              options={countryOptions}
              value={countryOptions.find(c => c.value === form.countryCode) || null}
              onChange={val => handleChange("countryCode", val ? val.value : "")}
              placeholder="Wybierz kraj"
              isClearable
              className="react-select-container"
              classNamePrefix="react-select"
              menuPlacement="auto"
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">Miasto*</label>
            <input className="input" name="city" value={form.city} onChange={e => handleChange("city", e.target.value)} required />
          </div>
          <div>
            <label className="block font-semibold mb-1">Cena (USD)*</label>
            <input className="input" name="roadPrice" value={form.roadPrice} onChange={e => handleChange("roadPrice", e.target.value)} type="number" required />
          </div>
          <div>
            <label className="block font-semibold mb-1">Tryb*</label>
            <select className="input" name="mode" value={form.mode} onChange={e => handleChange("mode", e.target.value)} required>
              {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          {form.mode === "FCL" && (
            <div>
              <label className="block font-semibold mb-1">Kontener*</label>
              <select className="input" name="containerType" value={form.containerType} onChange={e => handleChange("containerType", e.target.value)} required>
                {FCL_CONTAINER_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
              </select>
            </div>
          )}
          {form.mode === "LCL" && (
            <>
              <div>
                <label className="block text-xs">Min waga (kg)</label>
                <input className="input" name="minWeight" value={form.minWeight} onChange={e => handleChange("minWeight", e.target.value)} type="number" min="0" />
              </div>
              <div>
                <label className="block text-xs">Max waga (kg)</label>
                <input className="input" name="maxWeight" value={form.maxWeight} onChange={e => handleChange("maxWeight", e.target.value)} type="number" min="0" />
              </div>
              <div>
                <label className="block text-xs">Min obj. (m³)</label>
                <input className="input" name="minVolume" value={form.minVolume} onChange={e => handleChange("minVolume", e.target.value)} type="number" min="0" step="0.01" />
              </div>
              <div>
                <label className="block text-xs">Max obj. (m³)</label>
                <input className="input" name="maxVolume" value={form.maxVolume} onChange={e => handleChange("maxVolume", e.target.value)} type="number" min="0" step="0.01" />
              </div>
            </>
          )}
          <div>
            <label className="block font-semibold mb-1">Komentarz</label>
            <input className="input" name="comment" value={form.comment} onChange={e => handleChange("comment", e.target.value)} />
          </div>
          <div className="flex flex-col items-center">
            <label className="block text-xs">Aktywny</label>
            <input type="checkbox" name="active" checked={form.active} onChange={e => handleChange("active", e.target.checked)} />
          </div>
          <button className="btn ml-4" type="submit">{editId ? "Zapisz zmiany" : "Dodaj"}</button>
        </div>
        {error && <div className="text-red-500 mt-2 font-semibold">{error}</div>}
      </form>
      {/* --- TABELA --- */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border">
          <thead>
            <tr className="bg-blue-100">
              <th className="border px-2 py-1">Kraj</th>
              <th className="border px-2 py-1">Miasto</th>
              <th className="border px-2 py-1">Tryb</th>
              <th className="border px-2 py-1">Cena (USD)</th>
              <th className="border px-2 py-1">Zakresy / Kontenery</th>
              <th className="border px-2 py-1">Komentarz</th>
              <th className="border px-2 py-1">Aktywny</th>
              <th className="border px-2 py-1"></th>
            </tr>
          </thead>
          <tbody>
            {getFilteredGrouped().map(({ group, items }, idx) => {
              const groupKey = group.countryCode + group.city + group.mode;
              const inactive = isGroupInactive(items);
              return (
                <React.Fragment key={groupKey}>
                  <tr
                    className={
                      (inactive
                        ? "bg-gray-100 text-gray-400"
                        : "bg-blue-50 font-semibold cursor-pointer hover:bg-blue-200"
                      )
                    }
                    onClick={() => setExpandedRow(expandedRow === groupKey ? null : groupKey)}
                  >
                    <td className="border px-2 py-1 flex items-center gap-1">
                      <CountryFlag countryCode={group.countryCode} svg style={{ width: 18, height: 18 }} />{" "}
                      <span className="ml-1">{group.countryCode}</span>
                    </td>
                    <td className="border px-2 py-1">{group.city}</td>
                    <td className="border px-2 py-1">{group.mode}</td>
                    <td className="border px-2 py-1">{group.roadPrice}</td>
                    <td className="border px-2 py-1">
                      {group.mode === "FCL" ? "Zobacz kontenery" : "Zobacz zakresy"}
                    </td>
                    <td className="border px-2 py-1 truncate max-w-xs" title={group.comment}>{group.comment}</td>
                    <td className="border px-2 py-1 text-center">
                      {/* Brak checkboxa na grupie! */}
                      {inactive ? "Nieaktywny" : "Aktywny"}
                    </td>
                    <td className="border px-2 py-1">
                      <button className="btn" onClick={e => { e.stopPropagation(); handleEdit(group); }}>Edytuj</button>
                      <button className="btn-secondary" onClick={e => { e.stopPropagation(); handleDelete(group.id); }}>Usuń</button>
                    </td>
                  </tr>
                  {/* Rozwinięcie szczegółów */}
                  {expandedRow === groupKey && (
                    <tr>
                      <td colSpan={8} className="bg-white p-2">
                        <div className="flex mb-2">
                          <button
                            className="btn-secondary mr-2"
                            onClick={async e => {
                              e.stopPropagation();
                              await handleToggleAll(items, false);
                            }}
                          >
                            Dezaktywuj wszystko
                          </button>
                          <button
                            className="btn"
                            onClick={async e => {
                              e.stopPropagation();
                              await handleToggleAll(items, true);
                            }}
                          >
                            Aktywuj wszystko
                          </button>
                        </div>
                        <table className="w-full text-xs">
                          <thead>
                              <tr>
                                {group.mode === "FCL" ? (
                                  <>
                                    <th className="border px-2 py-1">Kontener</th>
                                    <th className="border px-2 py-1">Cena (USD)</th>
                                  </>
                                ) : (
                                  <>
                                    <th className="border px-2 py-1">Min waga</th>
                                    <th className="border px-2 py-1">Max waga</th>
                                    <th className="border px-2 py-1">Min obj.</th>
                                    <th className="border px-2 py-1">Max obj.</th>
                                    <th className="border px-2 py-1 font-semibold bg-blue-50">Cena (USD)</th>
                                  </>
                                )}
                                <th className="border px-2 py-1">Komentarz</th>
                                <th className="border px-2 py-1">Aktywny</th>
                                <th className="border px-2 py-1"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map(sub => (
                                <tr key={sub.id}>
                                  {group.mode === "FCL" ? (
                                    <>
                                      <td className="border px-2 py-1">{sub.containerType}</td>
                                      <td className="border px-2 py-1 font-semibold">{sub.roadPrice}</td>
                                    </>
                                  ) : (
                                    <>
                                      <td className="border px-2 py-1">{sub.minWeight}</td>
                                      <td className="border px-2 py-1">{sub.maxWeight === Infinity ? "∞" : sub.maxWeight}</td>
                                      <td className="border px-2 py-1">{sub.minVolume}</td>
                                      <td className="border px-2 py-1">{sub.maxVolume === Infinity ? "∞" : sub.maxVolume}</td>
                                      <td className="border px-2 py-1 font-semibold bg-blue-50">{sub.roadPrice}</td>
                                    </>
                                  )}
                                  <td className="border px-2 py-1">
                                    <input
                                      className="input w-36"
                                      value={sub.comment || ""}
                                      onChange={e => handleRangeCommentChange(sub.id, e.target.value)}
                                      placeholder="Komentarz"
                                    />
                                  </td>
                                  <td className="border px-2 py-1 text-center">
                                    <input type="checkbox" checked={!!sub.active} onChange={() => handleToggleActive(sub)} />
                                  </td>
                                  <td className="border px-2 py-1">
                                    <button className="btn" onClick={() => handleEdit(sub)}>Edytuj</button>
                                    <button className="btn-secondary" onClick={() => handleDelete(sub.id)}>Usuń</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {getFilteredGrouped().length === 0 && (
              <tr>
                <td colSpan={8} className="p-2 text-center text-gray-400">Brak wpisów dla wybranych filtrów.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
